import React, { useState, useMemo } from 'react';
import * as XLSX from 'xlsx';

type WizardStep = 1 | 2 | 3;

interface ImportRow {
  [key: string]: any;
}

interface GroupedInvoice {
  numeroInterno: string;
  lineas: ImportRow[];
  encabezado: ImportRow | null;
}

const expectedHeaders = [
  'Codigo actividad economica',
  'Fecha de emision',
  'Numero Interno',
  'Tipo de documento',
  'Condicion de venta',
  'Plazo de credito',
  'Medio de pago',
  'Moneda',
  'Tipo de cambio',
  'OtrasSenasExtranjero',
  'Registro Fiscal',
  'Dias_Pago',
  'Nombre receptor',
  'Tipo de cedula',
  'Cedula',
  'Provincia',
  'Canton',
  'Distrito',
  'Barrio',
  'Direccion',
  'Correo',
  'Copias',
  'Area',
  'Telefono',
  'Cod Actividad',
  'Cantidad',
  'Codigo del producto',
  'Codigo Cabys',
  'Unidad de medida',
  'Precio',
  'Detalle de linea',
  'Monto descuento',
  'Motivo de descuento',
  'Partida arancelaria',
  'FormaFarmaceutica',
  'RegistroMedicamento29',
  'NumeroVINoSerie31',
  'IVACobradoFabrica',
  'Linea_ImpuestoAsumidoEmisorFabrica',
  'BaseImponibleIVA',
  'Codigo del impuesto',
  'Porcentaje de impuesto',
  'Codigo de la tarifa',
  'Monto exportacion',
  'Tipo_Exo',
  'Numero_DocumentoExo',
  'Articulo',
  'Inciso',
  'FechaExo',
  'InstitucionExo',
  'PorcExonera',
  'MontoExonera',
  'Comentarios',
  'Consecutivo de referencia',
  'Tipo de accion de referencia',
  'Tipo de documento de referencia',
  'Razon de la nota',
  'Fecha de Referencia',
  'Cod Cargo',
  'Porcentaje Cargo',
  'Monto Cargo',
  'Identificacion Tercero',
  'Nombre Tercero',
  'Codigo Ref OtrosCargos',
  'Tipo Otros Cargos',
  'Correo de Recepción',
  'Cuenta Comisión',
  'Cuenta IVA'
];

const normalizeHeader = (h: string) => h.trim().replace(/\s+/g, ' ');

const normalizeCode = (v: any): string => {
  const s = String(v ?? '').trim();
  if (s.length === 0) return '';
  if (/^\d$/.test(s)) return `0${s}`;
  return s;
};

const docTypeMap: Record<string, string> = {
  '01': 'Factura electrónica',
  '02': 'Nota de débito electrónica',
  '03': 'Nota de crédito electrónica',
  '04': 'Tiquete electrónico',
  '05': 'Confirmación de aceptación del comprobante electrónico',
  '06': 'Confirmación de aceptación parcial del comprobante electrónico',
  '07': 'Confirmación de rechazo del comprobante electrónico',
  '08': 'Factura electrónica de compras',
  '09': 'Factura electrónica de exportación',
  '10': 'Recibo Electrónico de Pago'
};

const condicionVentaMap: Record<string, string> = {
  '01': 'Contado',
  '02': 'Crédito',
  '03': 'Consignación',
  '04': 'Apartado',
  '05': 'Arrendamiento con opción de compra',
  '06': 'Arrendamiento en función financiera',
  '07': 'Cobro a favor de un tercero',
  '08': 'Servicios prestados al Estado',
  '09': 'Pago de servicios prestado al Estado',
  '10': 'Venta a crédito en IVA hasta 90 días (Art. 27 LIVA)',
  '11': 'Pago de venta a crédito en IVA hasta 90 días (Art. 27 LIVA)',
  '12': 'Venta Mercancía No Nacionalizada',
  '13': 'Venta Bienes Usados No Contribuyente',
  '14': 'Arrendamiento Operativo',
  '15': 'Arrendamiento Financiero',
  '99': 'Otros'
};

const tipoCedulaMap: Record<string, string> = {
  '01': 'Cédula Física',
  '02': 'Cédula Jurídica',
  '03': 'DIMEX',
  '04': 'NITE',
  '05': 'Extranjero No Domiciliado',
  '06': 'No Contribuyente'
};

const medioPagoMap: Record<string, string> = {
  '01': 'Efectivo',
  '02': 'Tarjeta',
  '03': 'Cheque',
  '04': 'Transferencia – depósito bancario',
  '05': 'Recaudado por terceros',
  '06': 'SINPE MOVIL',
  '07': 'Plataforma Digital',
  '99': 'Otros'
};

const codigoImpuestoMap: Record<string, string> = {
  '01': 'Impuesto al Valor Agregado',
  '02': 'Impuesto Selectivo de Consumo',
  '03': 'Impuesto Único a los Combustibles',
  '04': 'Impuesto específico de Bebidas Alcohólicas',
  '05': 'Bebidas envasadas sin contenido alcohólico y jabones de tocador',
  '06': 'Impuesto a los Productos de Tabaco',
  '07': 'IVA (cálculo especial)',
  '08': 'IVA Régimen de Bienes Usados (Factor)',
  '12': 'Impuesto Específico al Cemento',
  '99': 'Otros'
};

const codigoTarifaMap: Record<string, string> = {
  '01': 'Tarifa 0% (Art. 32, num 1, RLIVA)',
  '02': 'Tarifa reducida 1% ',
  '03': 'Tarifa reducida 2% ',
  '04': 'Tarifa reducida 4% ',
  '05': 'Transitorio 0% ',
  '06': 'Transitorio 4% ',
  '07': 'Tarifa transitoria 8% ',
  '08': 'Tarifa general 13% ',
  '09': 'Tarifa reducida 0.5% ',
  '10': 'Tarifa Exenta',
  '11': 'Tarifa 0% sin derecho a crédito'
};

const tipoDocReferenciaMap: Record<string, string> = {
  '01': 'Factura electrónica',
  '02': 'Nota de débito electrónica',
  '03': 'Nota de crédito electrónica',
  '04': 'Tiquete electrónico',
  '05': 'Nota de despacho',
  '06': 'Contrato',
  '07': 'Procedimiento',
  '08': 'Comprobante emitido en contingencia',
  '09': 'Devolución mercadería',
  '10': 'Comprobante electrónico rechazado por MH',
  '11': 'Sustituye factura rechazada por el Receptor',
  '12': 'Sustituye Factura de exportación',
  '13': 'Facturación mes vencido',
  '14': 'Comprobante aportado por contribuyente Reg. Especial',
  '15': 'Sustituye Factura electrónica de Compra',
  '16': 'Comprobante de Proveedor No Domiciliado',
  '17': 'Nota de Crédito a Factura Electrónica de Compra',
  '18': 'Nota de Débito a Factura Electrónica de Compra',
  '99': 'Otros'
};

const tipoAccionReferenciaMap: Record<string, string> = {
  '01': 'Anula Documento de Referencia',
  '02': 'Corrige monto',
  '04': 'Referencia a otro documento',
  '05': 'Sustituye comprobante provisional por contingencia',
  '06': 'Devolución de mercancía',
  '07': 'Sustituye comprobante electrónico',
  '08': 'Factura Endosada',
  '09': 'Nota de crédito financiera',
  '10': 'Nota de débito financiera',
  '11': 'Proveedor No Domiciliado',
  '12': 'Crédito por exoneración posterior a la facturación',
  '99': 'Otros'
};

const decodeValue = (key: string, val: any): string => {
  const code = normalizeCode(val);
  switch (normalizeHeader(key)) {
    case 'Tipo de documento':
      return docTypeMap[code] ? `${docTypeMap[code]} (${code})` : String(val ?? '');
    case 'Condicion de venta':
      return condicionVentaMap[code] ? `${condicionVentaMap[code]} (${code})` : String(val ?? '');
    case 'Tipo de cedula':
      return tipoCedulaMap[code] ? `${tipoCedulaMap[code]} (${code})` : String(val ?? '');
    case 'Medio de pago':
      return medioPagoMap[code] ? `${medioPagoMap[code]} (${code})` : String(val ?? '');
    case 'Codigo del impuesto':
      return codigoImpuestoMap[code] ? `${codigoImpuestoMap[code]} (${code})` : String(val ?? '');
    case 'Codigo de la tarifa':
      return codigoTarifaMap[code] ? `${codigoTarifaMap[code]} (${code})` : String(val ?? '');
    case 'Tipo de documento de referencia':
      return tipoDocReferenciaMap[code] ? `${tipoDocReferenciaMap[code]} (${code})` : String(val ?? '');
    case 'Tipo de accion de referencia':
      return tipoAccionReferenciaMap[code] ? `${tipoAccionReferenciaMap[code]} (${code})` : String(val ?? '');
    default:
      return String(val ?? '');
  }
};

const ExcelImportWizard: React.FC = () => {
  const [step, setStep] = useState<WizardStep>(1);
  const [fileName, setFileName] = useState<string>('');
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const headersDetected = useMemo(() => {
    if (rows.length === 0) return [];
    return Object.keys(rows[0]).map(normalizeHeader);
  }, [rows]);

  const missingHeaders = useMemo(() => {
    const detectedSet = new Set(headersDetected);
    return expectedHeaders.filter((h) => !detectedSet.has(normalizeHeader(h)));
  }, [headersDetected]);

  const requiredFields = [
    'Numero Interno',
    'Nombre receptor',
    'Tipo de cedula',
    'Cedula',
    'Codigo Cabys',
    'Cantidad',
    'Precio'
  ];

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (evt) => {
      const data = evt.target?.result;
      if (!data) return;
      const workbook = XLSX.read(data, { type: 'binary' });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const json: ImportRow[] = XLSX.utils.sheet_to_json(sheet, { defval: '' });
      const normalized = json.map((row) => {
        const newRow: ImportRow = {};
        Object.entries(row).forEach(([k, v]) => {
          newRow[normalizeHeader(k)] = typeof v === 'string' ? v.trim() : v;
        });
        return newRow;
      });
      setRows(normalized);
      setStep(2);
    };
    reader.readAsBinaryString(file);
  };

  const validateRows = (): string[] => {
    const errors: string[] = [];

    if (rows.length === 0) {
      errors.push('El documento no contiene filas de datos.');
      return errors;
    }

    if (missingHeaders.length > 0) {
      errors.push(
        `Faltan columnas obligatorias: ${missingHeaders.join(', ')}`
      );
    }

    rows.forEach((r, idx) => {
      requiredFields.forEach((f) => {
        const val = r[normalizeHeader(f)];
        if (val === undefined || val === null || `${val}`.trim() === '') {
          errors.push(`Fila ${idx + 2}: campo obligatorio vacío → ${f}`);
        }
      });
      const fecha = r['Fecha de emision'];
      if (fecha && typeof fecha === 'string') {
        const ok = /(\d{2}|XX)-(\d{2}|XX)-(\d{4}|XXXX)/.test(fecha.trim());
        if (!ok) errors.push(`Fila ${idx + 2}: formato de fecha inválido`);
      }
      const cantidad = r['Cantidad'];
      if (cantidad && isNaN(Number(String(cantidad).replace(',', '.')))) {
        errors.push(`Fila ${idx + 2}: cantidad no numérica`);
      }
      const precio = r['Precio'];
      if (precio && isNaN(Number(String(precio).replace(',', '.')))) {
        errors.push(`Fila ${idx + 2}: precio no numérico`);
      }
    });

    return errors;
  };

  const agrupadas: GroupedInvoice[] = useMemo(() => {
    const byNumero = new Map<string, ImportRow[]>();
    rows.forEach((r) => {
      const numero = String(r['Numero Interno'] || '').trim();
      if (!numero) return;
      if (!byNumero.has(numero)) byNumero.set(numero, []);
      byNumero.get(numero)!.push(r);
    });
    const result: GroupedInvoice[] = [];
    byNumero.forEach((lineas, numeroInterno) => {
      const encabezado = lineas[0] || null;
      result.push({ numeroInterno, lineas, encabezado });
    });
    return result;
  }, [rows]);

  const handleValidarPlantilla = () => {
    const errs = validateRows();
    setValidationErrors(errs);
    if (errs.length === 0) setStep(3);
  };

  const handleVolver = () => {
    setStep((s) => (s > 1 ? ((s - 1) as WizardStep) : s));
  };

  const handleReiniciar = () => {
    setStep(1);
    setFileName('');
    setRows([]);
    setValidationErrors([]);
  };

  const renderKeyValueTable = (keys: string[], source: ImportRow | null) => {
    return (
      <div className="overflow-x-auto">
        <table className="min-w-full table-fixed border border-gray-300">
          <thead>
            <tr>
              <th className="w-1/2 border px-3 py-2 text-left text-xs font-medium text-gray-500">Campo</th>
              <th className="w-1/2 border px-3 py-2 text-left text-xs font-medium text-gray-500">Valor</th>
            </tr>
          </thead>
          <tbody>
            {keys.map((k) => (
              <tr key={k}>
                <td className="border px-3 py-2 text-gray-700">{k}</td>
                <td className="border px-3 py-2 font-medium break-words">{decodeValue(k, source?.[k])}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Importación de Plantilla de Facturas (CR v4.4)
          </h2>

          <div className="flex items-center mb-6">
            <div className={`flex-1 text-center p-2 rounded ${step >= 1 ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-500'}`}>1. Seleccionar archivo</div>
            <div className="w-8 border-t mx-2 border-gray-300" />
            <div className={`flex-1 text-center p-2 rounded ${step >= 2 ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-500'}`}>2. Validar plantilla</div>
            <div className="w-8 border-t mx-2 border-gray-300" />
            <div className={`flex-1 text-center p-2 rounded ${step >= 3 ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-500'}`}>3. Listar facturas</div>
          </div>

          {step === 1 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Archivo .xlsx o .csv</label>
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileChange}
                className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
              />
              {fileName && (
                <p className="mt-2 text-sm text-gray-600">Seleccionado: {fileName}</p>
              )}
            </div>
          )}

          {step === 2 && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Validación de Plantilla</h3>
                <div className="space-x-2">
                  <button onClick={handleVolver} className="px-4 py-2 rounded bg-gray-100 text-gray-700 hover:bg-gray-200">Volver</button>
                  <button onClick={handleReiniciar} className="px-4 py-2 rounded bg-gray-100 text-gray-700 hover:bg-gray-200">Reiniciar</button>
                  <button onClick={handleValidarPlantilla} className="px-4 py-2 rounded bg-primary-600 text-white hover:bg-primary-700">Validar</button>
                </div>
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-700">Columnas detectadas ({headersDetected.length}):</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {headersDetected.map((h) => (
                    <span key={h} className="px-2 py-1 rounded bg-gray-100 text-gray-700 text-xs">{h}</span>
                  ))}
                </div>
              </div>

              {missingHeaders.length > 0 ? (
                <div className="p-4 rounded bg-red-50 border border-red-200 text-red-700">
                  <p className="font-medium">Faltan columnas obligatorias:</p>
                  <ul className="list-disc ml-6 mt-2">
                    {missingHeaders.map((h) => <li key={h}>{h}</li>)}
                  </ul>
                </div>
              ) : (
                <div className="p-4 rounded bg-green-50 border border-green-200 text-green-700">
                  <p>La estructura de la plantilla es válida.</p>
                </div>
              )}

              {validationErrors.length > 0 && (
                <div className="mt-4 p-4 rounded bg-yellow-50 border border-yellow-200 text-yellow-700">
                  <p className="font-medium">Problemas encontrados:</p>
                  <ul className="list-disc ml-6 mt-2 max-h-64 overflow-auto">
                    {validationErrors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Facturas agrupadas por "Numero Interno"</h3>
                <div className="space-x-2">
                  <button onClick={handleVolver} className="px-4 py-2 rounded bg-gray-100 text-gray-700 hover:bg-gray-200">Volver</button>
                  <button onClick={handleReiniciar} className="px-4 py-2 rounded bg-gray-100 text-gray-700 hover:bg-gray-200">Reiniciar</button>
                </div>
              </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Numero Interno</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Nombre receptor</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Lineas</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Monto total estimado</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {agrupadas.map((g) => {
                  const nombre = g.encabezado?.['Nombre receptor'] || '';
                  const total = g.lineas.reduce((acc, r) => {
                    const cantidad = Number(String(r['Cantidad'] || '0').replace(',', '.')) || 0;
                    const precio = Number(String(r['Precio'] || '0').replace(',', '.')) || 0;
                    return acc + cantidad * precio;
                  }, 0);
                  return (
                    <>
                      <tr key={g.numeroInterno}>
                        <td className="px-4 py-2 text-sm text-gray-700">{g.numeroInterno}</td>
                        <td className="px-4 py-2 text-sm text-gray-700">{nombre}</td>
                        <td className="px-4 py-2 text-sm text-gray-700">{g.lineas.length}</td>
                        <td className="px-4 py-2 text-sm text-gray-700">{total.toLocaleString('es-CR', { style: 'currency', currency: 'CRC' })}</td>
                        <td className="px-4 py-2 text-sm">
                          <button
                            onClick={() => setExpanded((prev) => ({ ...prev, [g.numeroInterno]: !prev[g.numeroInterno] }))}
                            className="px-3 py-1 rounded bg-primary-600 text-white hover:bg-primary-700"
                          >
                            {expanded[g.numeroInterno] ? 'Ocultar detalle' : 'Ver detalle'}
                          </button>
                        </td>
                      </tr>
                      {expanded[g.numeroInterno] && (
                        <tr>
                          <td colSpan={5} className="px-4 py-4 bg-gray-50">
                            <div className="space-y-6">
                              <div>
                                <h4 className="text-md font-semibold text-gray-900 mb-2">Encabezado</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div className="bg-white rounded border p-4">
                                    <div className="text-sm font-medium text-gray-700 mb-2">Generales</div>
                                    {renderKeyValueTable([
                                      'Codigo actividad economica',
                                      'Fecha de emision',
                                      'Tipo de documento',
                                      'Condicion de venta',
                                      'Plazo de credito',
                                      'Medio de pago',
                                      'Moneda',
                                      'Tipo de cambio',
                                      'OtrasSenasExtranjero',
                                      'Registro Fiscal',
                                      'Dias_Pago'
                                    ], g.encabezado)}
                                  </div>
                                  <div className="bg-white rounded border p-4">
                                    <div className="text-sm font-medium text-gray-700 mb-2">Receptor</div>
                                    {renderKeyValueTable([
                                      'Nombre receptor',
                                      'Tipo de cedula',
                                      'Cedula',
                                      'Provincia',
                                      'Canton',
                                      'Distrito',
                                      'Barrio',
                                      'Direccion',
                                      'Correo',
                                      'Copias',
                                      'Area',
                                      'Telefono',
                                      'Cod Actividad'
                                    ], g.encabezado)}
                                  </div>
                                </div>
                              </div>

                              <div>
                                <h4 className="text-md font-semibold text-gray-900 mb-2">Detalle de la factura</h4>
                                <div className="space-y-3">
                                  {g.lineas.map((l, idx) => (
                                    <div key={idx} className="bg-white rounded border p-4">
                                      <div className="text-sm font-medium text-gray-700 mb-2">Línea {idx + 1}</div>
                                      {renderKeyValueTable([
                                        'Cantidad',
                                        'Codigo del producto',
                                        'Codigo Cabys',
                                        'Unidad de medida',
                                        'Precio',
                                        'Detalle de linea',
                                        'Monto descuento',
                                        'Motivo de descuento',
                                        'Partida arancelaria',
                                        'FormaFarmaceutica',
                                        'RegistroMedicamento29',
                                        'NumeroVINoSerie31',
                                        'IVACobradoFabrica',
                                        'Linea_ImpuestoAsumidoEmisorFabrica',
                                        'BaseImponibleIVA',
                                        'Codigo del impuesto',
                                        'Porcentaje de impuesto',
                                        'Codigo de la tarifa',
                                        'Monto exportacion',
                                        'Tipo_Exo',
                                        'Numero_DocumentoExo',
                                        'Articulo',
                                        'Inciso',
                                        'FechaExo',
                                        'InstitucionExo',
                                        'PorcExonera',
                                        'MontoExonera'
                                      ], l)}
                                    </div>
                                  ))}
                                </div>
                              </div>

                              <div>
                                <h4 className="text-md font-semibold text-gray-900 mb-2">Informacion de referencia</h4>
                                <div className="bg-white rounded border p-4">
                                  {renderKeyValueTable([
                                    'Consecutivo de referencia',
                                    'Tipo de accion de referencia',
                                    'Tipo de documento de referencia',
                                    'Razon de la nota',
                                    'Fecha de Referencia'
                                  ], g.encabezado)}
                                </div>
                              </div>

                              <div>
                                <h4 className="text-md font-semibold text-gray-900 mb-2">Otros Cargos</h4>
                                <div className="space-y-3">
                                  {g.lineas
                                    .filter((l) => {
                                      const keys = ['Cod Cargo','Porcentaje Cargo','Monto Cargo','Identificacion Tercero','Nombre Tercero','Codigo Ref OtrosCargos','Tipo Otros Cargos'];
                                      return keys.some((k) => {
                                        const v = l[k];
                                        return v !== undefined && v !== null && String(v).trim() !== '';
                                      });
                                    })
                                    .map((l, idx) => (
                                      <div key={idx} className="bg-white rounded border p-4">
                                        {renderKeyValueTable([
                                          'Cod Cargo',
                                          'Porcentaje Cargo',
                                          'Monto Cargo',
                                          'Identificacion Tercero',
                                          'Nombre Tercero',
                                          'Codigo Ref OtrosCargos',
                                          'Tipo Otros Cargos'
                                        ], l)}
                                      </div>
                                    ))}
                                </div>
                              </div>

                              <div>
                                <h4 className="text-md font-semibold text-gray-900 mb-2">Informacion adicional</h4>
                                <div className="bg-white rounded border p-4">
                                  {renderKeyValueTable([
                                    'Correo de Recepción',
                                    'Cuenta Comisión',
                                    'Cuenta IVA'
                                  ], g.encabezado)}
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExcelImportWizard;